import React, { useEffect, useState } from "react";
import writeXlsxFile from "write-excel-file";
import Toast from "../../../../components/toast";
import { toast } from "react-toastify";

import _ from "lodash";
import cx from "classnames";
import styles from "./downloadReports.module.scss";

interface IExportProps {
  schema: ISchema[];
  fetchDataForPage: (page: number) => void;
  fileName: string;
  title: string;
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
  invalidateData: () => void;
  data: any;
  fetchState: {
    loading: boolean;
    succeeded: boolean;
    error: any;
  };
  disabled?: boolean;
  handleClose?: () => void;
  downloadClick?: () => void;
}
interface ISchema {
  column: string;
  type: any;
  value: any;
}

const DownloadReports: React.FC<IExportProps> = (props) => {
  const schema = props.schema;
  const data = props.data;
  const fetchDataForPage = props.fetchDataForPage;
  const { hasMoreItems, currentPage, totalRecordsCount } = props.pagination;
  const { loading, succeeded, error } = props.fetchState;

  const [fetchData, setFetchData] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [downloadedPercentage, setDownloadedPercentage] = useState<number>(1);
  const [showToast, setShowToast] = useState<boolean>(false);

  useEffect(() => {
    if (fetchData && !error && !downloading && succeeded) {
      if (hasMoreItems && !loading) {
        fetchDataForPage(currentPage + 1);
      } else if (!hasMoreItems && !downloading) {
        triggerDownload();
      }

      if (_.size(data) > 0) {
        const percentage = Math.floor((_.size(data) / totalRecordsCount) * 100);
        setDownloadedPercentage(percentage);
      }
    }

    if (error) {
      invalidateDownloadData();
      toast(<Toast.Failure message={`Failed to download ${props.title}`} />, {
        autoClose: 3000,
      });
    }
  }, [succeeded, error]);

  useEffect(() => {
    return () => {
      invalidateDownloadData();
    };
  }, []);

  const triggerDownload = async () => {
    if (!downloading) {
      setDownloading(true);
      await writeXlsxFile(data, {
        schema,
        fileName: props.fileName,
      })
        .then(() => {
          setDownloading(false);
          invalidateDownloadData();
          if (props.handleClose) {
            props.handleClose();
          }
          toast(
            <Toast.Sucess message={`${props.title} Downloaded Successfully`} />,
            {
              toastId: props.fileName,
            },
          );
        })
        .catch((error) => {
          setDownloading(false);
          invalidateDownloadData();
          toast(
            <Toast.Failure message={`Failed to download ${props.title}`} />,
            {
              autoClose: 3000,
            },
          );
        });
    }
  };

  const handleDownloadClick = async () => {
    if (props.downloadClick) {
      props.downloadClick();
    }
    setShowToast(true);
  };

  const handleToastClose = () => {
    invalidateDownloadData();
  };

  const handleConfirmClick = () => {
    fetchDataForPage(0);
    setFetchData(true);
  };

  const invalidateDownloadData = () => {
    setFetchData(false);
    setShowToast(false);
    setDownloading(false);
    setDownloadedPercentage(1);
    props.invalidateData();
  };

  return (
    <div className={styles.downloadContainer}>
      <button
        disabled={loading || showToast || props.disabled}
        onClick={handleDownloadClick}
        className={cx(styles.downloadButton, {
          [styles.disabled]: loading || showToast || props.disabled,
        })}
      >
        <i className="paperflite-download" />
        <span> download </span>
      </button>

      <div
        className={cx(styles.toast, {
          [styles.show]: showToast,
        })}
      >
        <p className={styles.header}>
          would you like to export the
          <i
            onClick={handleToastClose}
            className={cx("paperflite-close", { [styles.disabled]: fetchData })}
          />{" "}
        </p>
        <p className={styles.title}>{props.title}</p>

        {!fetchData && (
          <p onClick={handleConfirmClick} className={styles.confirm}>
            <i className="paperflite-tick" /> Confirm
          </p>
        )}

        {fetchData && (
          <div className={styles.progressBar}>
            <div
              className={styles.progress}
              style={{ width: `${downloadedPercentage}%` }}
            ></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadReports;
